import { useEffect, useState } from 'react';
import { Get, Post, Send } from '../utils/RemoteAuthority';
import { RefreshLobby, SetTitle } from '../utils/Lobby';
import TopBar from './TopBar';
import { useNewIcons, useNewUI, useStaticButtons } from '../utils/Preferences';
import { PushIsOn, PushSupported, TurnPushOff, TurnPushOn, WatchPushPermission } from '../utils/Push';
import { Colors, ColorStrong } from './Constants/SeatColors';
import './Lobby.css';

const NoAnswer = { ok: false, error: 'The game server is not answering.' };

// Your profile: the name you go by (in your games too), the color you see yourself in and, with a password kept here, a
// new password. Your username stays as it is. Signed in another way (e.g. Tailscale), your name comes from there and is only shown.
function Profile()
{
    const [me, setMe] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        SetTitle('Profile · Through the Ages');
        Get('/games/me').then(result => result.ok === false ? setError(result.error) : setMe(result)).catch(() => setError(NoAnswer.error));
    }, []);

    return <div className="tta-lobby">
        <TopBar page="profile"/>
        <main className="tta-lobby-main">
            <h1 className="tta-lobby-heading">Profile</h1>
            {error && <div className="tta-lobby-error">{error}</div>}
            {!me && !error && <div className="tta-muted">Loading…</div>}
            {me && <>
                <section className="tta-lobby-section">
                    <h2 className="tta-lobby-section-title">Name</h2>
                    <div className="tta-panel tta-profile-panel">
                        {me.editableName ? <NameForm me={me} OnSaved={(name) => setMe({ ...me, name })}/>
                            : <p>{me.name} <span className="tta-muted">(from the way you sign in)</span></p>}
                        <div className="tta-muted tta-field-note">Username {me.login}: you sign in with it, and it can't be changed.</div>
                    </div>
                </section>
                <section className="tta-lobby-section">
                    <h2 className="tta-lobby-section-title">Your color</h2>
                    <div className="tta-panel tta-profile-panel"><ColorForm me={me} OnSaved={(color) => setMe({ ...me, color })}/></div>
                </section>
                {me.form && <section className="tta-lobby-section">
                    <h2 className="tta-lobby-section-title">Password</h2>
                    <div className="tta-panel tta-profile-panel"><PasswordForm/></div>
                </section>}
                <section className="tta-lobby-section">
                    <h2 className="tta-lobby-section-title">Notifications</h2>
                    <div className="tta-panel tta-profile-panel"><NotificationSettings/><SlackSettings/></div>
                </section>
                <section className="tta-lobby-section">
                    <h2 className="tta-lobby-section-title">Playing</h2>
                    <div className="tta-panel tta-profile-panel"><PlayingSettings/></div>
                </section>
            </>}
        </main>
    </div>;
}

// The name you go by: everyone sees it, in every game you are in, the ones already started too
function NameForm({ me, OnSaved })
{
    const [name, setName] = useState(me.name);
    const [note, setNote] = useState(null);
    const [error, setError] = useState(null);
    const changed = name.trim() !== '' && name.trim().replace(/\s+/g, ' ') !== me.name;
    const save = async () => {
        const result = await Send('PUT', '/games/me', { name }).catch(() => NoAnswer);
        if (result.ok === false) return setError(result.error);
        setError(null);
        setName(result.name);
        setNote('Saved. Games open in other tabs show it once they are reloaded.');
        OnSaved(result.name);
        RefreshLobby();
    };
    return <>
        <label className="tta-field">
            <span className="tta-field-label">The name you go by</span>
            <div className="tta-field-row">
                <input className="tta-input" value={name} maxLength={40}
                    onChange={(e) => { setName(e.target.value); setNote(null); }}
                    onKeyDown={(e) => e.key === 'Enter' && changed && save()}/>
                <button className="tta-primary-button" disabled={!changed} onClick={save}>Save</button>
            </div>
            <span className="tta-muted tta-field-note">Everyone sees it, in all your games, the ones already started too.</span>
        </label>
        {note && <div className="tta-admin-note">{note}</div>}
        {error && <div className="tta-lobby-error">{error}</div>}
    </>;
}

// The color you see yourself in, in every game (the others take the rest); none: your seat's, as everyone else sees it.
// Only you see it, so it is never the way the game tells you apart from anyone.
function ColorForm({ me, OnSaved })
{
    const [error, setError] = useState(null);
    const save = async (color) => {
        const result = await Send('PUT', '/games/me', { color }).catch(() => NoAnswer);
        if (result.ok === false) return setError(result.error);
        setError(null);
        OnSaved(result.color);
        RefreshLobby();
    };
    const choices = [...Colors.map(color => ({ color, label: color })), { color: null, label: 'By seat' }];
    return <div className="tta-field">
        <div className="tta-color-choices">
            {choices.map(({ color, label }) =>
                <button key={label} type="button" className={`tta-color-choice${(me.color ?? null) === color ? ' chosen' : ''}`}
                    onClick={() => save(color)} aria-pressed={(me.color ?? null) === color}>
                    <span className="tta-seat-dot" style={color ? { background: ColorStrong(color) } : undefined}/>{label}
                </button>)}
        </div>
        <span className="tta-muted tta-field-note">
            You see yourself in this color in every game, and the other players in the rest. Only you see it: everyone else
            sees their own. By seat: the first player blue, then red, green and yellow.
        </span>
        {error && <div className="tta-lobby-error">{error}</div>}
    </div>;
}

// A new password, given the one now; every other browser you are signed in on is signed out
function PasswordForm()
{
    const [current, setCurrent] = useState('');
    const [password, setPassword] = useState('');
    const [repeated, setRepeated] = useState('');
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState(null);
    const [error, setError] = useState(null);
    const mismatch = repeated !== '' && repeated !== password;
    const ready = current !== '' && password.length >= 8 && password === repeated && !busy;
    const save = async () => {
        setBusy(true);
        const result = await Post('/auth/password/change', { current, password }).catch(() => NoAnswer);
        setBusy(false);
        if (result.ok === false) return setError(result.error);
        setError(null);
        setCurrent(''); setPassword(''); setRepeated('');
        setNote('Saved. Any other browsers you were signed in on are signed out.');
    };
    const field = (label, value, set, autoComplete, note) => <label className="tta-field">
        <span className="tta-field-label">{label}</span>
        <input className="tta-input" type="password" value={value} autoComplete={autoComplete}
            onChange={(e) => { set(e.target.value); setNote(null); }}/>
        {note && <span className="tta-muted tta-field-note">{note}</span>}
    </label>;
    return <form onSubmit={(e) => { e.preventDefault(); if (ready) save(); }}>
        {field('Your password now', current, setCurrent, 'current-password')}
        {field('New password', password, setPassword, 'new-password', 'At least 8 characters.')}
        {field('New password again', repeated, setRepeated, 'new-password', mismatch ? 'Not the same as the new password.' : null)}
        <button className="tta-primary-button" type="submit" disabled={!ready}>Change password</button>
        {note && <div className="tta-admin-note">{note}</div>}
        {error && <div className="tta-lobby-error">{error}</div>}
    </form>;
}

// A notification from this browser when a game comes to wait on you, with no tab of it open (see Push.js). Each browser has
// its own. The switch only stays on if notifications really are on: the browser refusing permission, then or later, turns it off.
function NotificationSettings()
{
    const [on, setOn] = useState(null);
    // (what was just asked for, shown while the browser asks its permission and the server hears of it)
    const [wanted, setWanted] = useState(null);
    const [error, setError] = useState(null);
    useEffect(() => {
        if (!PushSupported()) return undefined;
        PushIsOn().then(setOn).catch(() => setOn(false));
        return WatchPushPermission(() => setOn(false));
    }, []);
    if (!PushSupported())
    {
        return <div className="tta-muted">This browser can't show notifications from the game.</div>;
    }
    const change = async (turnOn) => {
        setWanted(turnOn);
        setError(null);
        try {
            await (turnOn ? TurnPushOn() : TurnPushOff());
            setOn(turnOn);
        } catch (e) {
            setError(e.message);
        }
        setWanted(null);
    };
    const busy = wanted !== null;
    return <div className="tta-field">
        <label className="tta-switch">
            <input type="checkbox" checked={wanted ?? on ?? false} disabled={busy || on === null} onChange={(e) => change(e.target.checked)}/>
            <span>Browser notifications</span>
        </label>
        <span className="tta-muted tta-field-note">
            A notification from this browser when it's your turn or you have a decision to make, even with no game open. They
            only arrive while the browser is running; ones sent while it is closed come when it next starts, for up to two days.
            Set for each browser.
        </span>
        {error && <div className="tta-lobby-error">{error}</div>}
    </div>;
}

// A direct message in SEP Slack when a game comes to wait on you (see the API's slack.js). The switch only stays on once the
// server has found them there and a first message has gone through.
function SlackSettings()
{
    const [slack, setSlack] = useState(null);
    // (the switch clicked on: asking for the email, which turns it on. It goes back off if that fails, the email left to correct)
    const [asking, setAsking] = useState(false);
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        Get('/games/slack').then(result => result.ok === false ? setError(result.error) : setSlack(result)).catch(() => setError(NoAnswer.error));
    }, []);
    const turnOn = async () => {
        setBusy(true);
        setError(null);
        const result = await Send('PUT', '/games/slack', { email }).catch(() => NoAnswer);
        setBusy(false);
        if (result.ok === false) return setError(result.error);
        setSlack({ ...slack, on: true, email: result.email });
        setAsking(false);
    };
    const turnOff = async () => {
        setBusy(true);
        setError(null);
        const result = await Send('DELETE', '/games/slack').catch(() => NoAnswer);
        setBusy(false);
        if (result.ok === false) return setError(result.error);
        setSlack({ ...slack, on: false, email: null });
    };
    const change = (checked) => {
        setError(null);
        if (slack.on) return turnOff();
        setAsking(checked);
    };
    const available = slack?.available;
    return <div className="tta-field">
        <label className="tta-switch">
            <input type="checkbox" checked={!!slack?.on || (asking && !error)} disabled={!available || busy} onChange={(e) => change(e.target.checked)}/>
            <span>Slack notifications</span>
        </label>
        <span className="tta-muted tta-field-note">
            {slack && !available
                ? "Slack notifications aren't set up on this server."
                : slack?.on
                    ? <>A direct message in SEP Slack when it's your turn or you have a decision to make, sent to {slack.email}'s account.</>
                    : "A direct message in SEP Slack when it's your turn or you have a decision to make. Only the SEP Slack workspace works: no other Slack can."}
        </span>
        {asking && !slack?.on && <label className="tta-field">
            <span className="tta-field-label">Your email in SEP Slack</span>
            <div className="tta-field-row">
                <input className="tta-input" type="email" value={email} maxLength={254} autoFocus
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && email.trim() && !busy && turnOn()}/>
                <button className="tta-primary-button" disabled={!email.trim() || busy} onClick={turnOn}>Turn on</button>
            </div>
            <span className="tta-muted tta-field-note">
                The email on your account in the SEP Slack workspace: it won't work for any other Slack. They stay off until
                this is done, and you'll get a direct message there to confirm. Only you and whoever runs the site can see it.
            </span>
        </label>}
        {error && <div className="tta-lobby-error">{error}</div>}
    </div>;
}

// How your games are shown, kept in this browser (see Preferences.js)
function PlayingSettings()
{
    const [staticButtons, setStaticButtons] = useStaticButtons();
    const [newUI, setNewUI] = useNewUI();
    const [newIcons, setNewIcons] = useNewIcons();
    return <>
        <div className="tta-field">
            <label className="tta-switch">
                <input type="checkbox" checked={staticButtons} onChange={(e) => setStaticButtons(e.target.checked)}/>
                <span>Use static buttons</span>
            </label>
            <span className="tta-muted tta-field-note">
                A fixed button for each kind of action under your board (Build, Upgrade, Develop…): click one, then pick what to do
                it with on your board or in your hand. In place of the action panel's buttons for those actions. Kept in this
                browser.
            </span>
        </div>
        <div className="tta-field">
            <label className="tta-switch">
                <input type="checkbox" checked={newUI} onChange={(e) => setNewUI(e.target.checked)}/>
                <span>Use new UI</span>
            </label>
            <span className="tta-muted tta-field-note">
                The culture and military boards under the card row, for everyone: tracks of culture, culture each turn and
                strength, and the tactics in play. In place of the military area on each board. Still being tried out; kept in
                this browser.
            </span>
        </div>
        <div className="tta-field">
            <label className="tta-switch">
                <input type="checkbox" checked={newIcons} onChange={(e) => setNewIcons(e.target.checked)}/>
                <span>Use new icons</span>
            </label>
            <span className="tta-muted tta-field-note">
                The game's small pictures (building types, tokens, culture, science…) redrawn sharp, in place of the old
                pixelated ones. Still being tried out; kept in this browser.
            </span>
        </div>
    </>;
}

export default Profile;
